import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import './DrawerManager.css';

/**
 * Drawer Manager Context
 * Manages multiple drawers/modals with proper z-index stacking and animations
 */

const DrawerContext = createContext();

export const useDrawer = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('useDrawer must be used within DrawerProvider');
  }
  return context;
};

export const DrawerProvider = ({ children }) => {
  const [drawers, setDrawers] = useState([]);

  const openDrawer = useCallback((drawer) => {
    const id = drawer.id || `drawer-${Date.now()}-${Math.random()}`;
    setDrawers(prev => [...prev, { ...drawer, id }]);
    return id;
  }, []);

  const closeDrawer = useCallback((id) => {
    setDrawers(prev => prev.filter(d => d.id !== id));
  }, []);

  const closeAllDrawers = useCallback(() => {
    setDrawers([]);
  }, []);

  const closeTopDrawer = useCallback(() => {
    setDrawers(prev => prev.slice(0, -1));
  }, []);

  // Handle escape key to close top drawer
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && drawers.length > 0) {
        closeTopDrawer();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [drawers.length, closeTopDrawer]);

  return (
    <DrawerContext.Provider value={{ openDrawer, closeDrawer, closeAllDrawers, closeTopDrawer, drawers }}>
      {children}
      <DrawerContainer drawers={drawers} closeDrawer={closeDrawer} />
    </DrawerContext.Provider>
  );
};

const DrawerContainer = ({ drawers, closeDrawer }) => {
  if (drawers.length === 0) return null;

  return (
    <>
      {drawers.map((drawer, index) => {
        const zIndexBase = 10000;
        const backdropZIndex = zIndexBase + (index * 2);
        const drawerZIndex = backdropZIndex + 1;
        const isTopDrawer = index === drawers.length - 1;

        return (
          <React.Fragment key={drawer.id}>
            {/* Backdrop */}
            <div
              className="drawer-backdrop"
              style={{
                zIndex: backdropZIndex,
                opacity: isTopDrawer ? 0.5 : 0,
                pointerEvents: isTopDrawer ? 'auto' : 'none'
              }}
              onClick={() => {
                if (isTopDrawer && drawer.closeOnBackdropClick !== false) {
                  closeDrawer(drawer.id);
                }
              }}
            />

            {/* Drawer */}
            <div
              className={`drawer drawer-${drawer.width || 'md'} drawer-slide-in`}
              style={{
                zIndex: drawerZIndex,
                width: drawer.customWidth || undefined
              }}
            >
              {/* Header */}
              <div className="drawer-header">
                <h2 className="drawer-title">{drawer.title}</h2>
                <button
                  className="drawer-close-btn"
                  onClick={() => closeDrawer(drawer.id)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {/* Content */}
              <div className="drawer-content">
                {typeof drawer.content === 'function'
                  ? drawer.content({ closeDrawer: () => closeDrawer(drawer.id) })
                  : drawer.content}
              </div>

              {/* Footer (optional) */}
              {drawer.footer && (
                <div className="drawer-footer">
                  {typeof drawer.footer === 'function'
                    ? drawer.footer({ closeDrawer: () => closeDrawer(drawer.id) })
                    : drawer.footer}
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </>
  );
};

/**
 * Standalone Drawer Component (for direct use without context)
 * Automatically calculates z-index to appear above existing drawers
 */
export const Drawer = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  width = 'md',
  customWidth,
  closeOnBackdropClick = true,
  className = '',
  drawerContentRef
}) => {
  // Debug: Log props to verify they're being passed
  if (title === 'Candidate Profile') {
    console.log('Drawer props:', { width, customWidth, className });
  }
  // Get drawer context if available (null if not in DrawerProvider)
  const drawerContext = useContext(DrawerContext);

  // Calculate z-index based on existing drawers
  const [zIndex, setZIndex] = useState({ backdrop: 10000, drawer: 10001 });

  useEffect(() => {
    if (!isOpen) return;

    const updateZIndex = () => {
      let backdropZIndex, drawerZIndex;
      const zIndexBase = 10000;

      // Count all drawers from multiple sources
      let totalDrawerCount = 0;

      // 1. DrawerProvider-managed drawers
      if (drawerContext && drawerContext.drawers && Array.isArray(drawerContext.drawers)) {
        totalDrawerCount += drawerContext.drawers.length;
      }

      // 2. Standalone drawers (count elements in DOM)
      const existingDrawers = document.querySelectorAll('.drawer');
      totalDrawerCount += existingDrawers.length;

      // 3. Find maximum z-index currently in use
      let maxZIndex = zIndexBase;
      existingDrawers.forEach(drawer => {
        const drawerZ = parseInt(window.getComputedStyle(drawer).zIndex);
        if (!isNaN(drawerZ) && drawerZ > maxZIndex) {
          maxZIndex = drawerZ;
        }
      });

      const existingBackdrops = document.querySelectorAll('.drawer-backdrop');
      existingBackdrops.forEach(backdrop => {
        const backdropZ = parseInt(window.getComputedStyle(backdrop).zIndex);
        if (!isNaN(backdropZ) && backdropZ > maxZIndex) {
          maxZIndex = backdropZ;
        }
      });

      // Set z-index higher than any existing drawer/backdrop
      backdropZIndex = Math.max(maxZIndex + 2, zIndexBase + (totalDrawerCount * 2) + 2);
      drawerZIndex = backdropZIndex + 1;
      
      setZIndex({ backdrop: backdropZIndex, drawer: drawerZIndex });
    };

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(updateZIndex);
    const timeoutId = setTimeout(updateZIndex, 10);

    return () => clearTimeout(timeoutId);
  }, [isOpen, drawerContext]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="drawer-backdrop"
        style={{ zIndex: zIndex.backdrop }}
        onClick={() => {
          if (closeOnBackdropClick) {
            onClose();
          }
        }}
      />

      {/* Drawer */}
      <div
        className={`drawer ${customWidth || title === 'Candidate Profile' || title === 'Interview Availability Coordination' ? 'drawer-custom' : `drawer-${width}`} drawer-slide-in ${className}`.trim()}
        style={{ 
          width: (title === 'Candidate Profile' || title === 'Interview Availability Coordination') ? '900px' : (customWidth || undefined),
          maxWidth: (customWidth || title === 'Candidate Profile' || title === 'Interview Availability Coordination') ? '900px' : undefined,
          height: '100vh',
          zIndex: zIndex.drawer
        }}
      >
        {/* Header */}
        <div className="drawer-header">
          <h2 className="drawer-title">{title}</h2>
          <button
            className="drawer-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div 
          ref={drawerContentRef}
          className="drawer-content"
          onScroll={(e) => {
            // Prevent scroll from jumping during updates by maintaining scroll position
            if (drawerContentRef && drawerContentRef.current) {
              const savedScroll = drawerContentRef.current.getAttribute('data-saved-scroll');
              if (savedScroll && Math.abs(e.target.scrollTop - parseFloat(savedScroll)) > 100) {
                // If scroll jumped significantly, restore it
                e.target.scrollTop = parseFloat(savedScroll);
              }
            }
          }}
        >
          {children}
        </div>

        {/* Footer (optional) */}
        {footer && (
          <div className="drawer-footer">
            {footer}
          </div>
        )}
      </div>
    </>
  );
};

export default Drawer;
